import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { ALWAYS_VISIBLE_COLUMNS, type ColumnId } from '../lib/constants'
import { useGanttStore } from '../store/ganttStore'
import { COLUMN_ORDER, columnLabelKey, columnWidth, isNumericColumn } from './gantt-columns'

interface ResizeHandleProps {
  id: ColumnId
  width: number
  onResize: (id: ColumnId, px: number) => void
  onAutoFit: (id: ColumnId) => void
}

/**
 * A header cell's right-edge resize handle: drag to resize the column, double-click to auto-fit it
 * (Task 2, Freelo #32253593). Pointer handling mirrors `Splitter.tsx` (pointer capture, `origin =
 * { x, width }`), with `useBarDrag.ts`'s rAF throttle on top — one `onResize` call per animation
 * frame instead of one per native pointermove.
 */
const ColumnResizeHandle: React.FC<ResizeHandleProps> = ({ id, width, onResize, onAutoFit }) => {
  const { t } = useTranslation()
  const [dragging, setDragging] = useState(false)
  const origin = useRef({ x: 0, width: 0 })
  const pendingPx = useRef<number | null>(null)
  const frameRef = useRef<number | null>(null)

  const flush = useCallback(() => {
    frameRef.current = null
    const px = pendingPx.current
    pendingPx.current = null
    if (px != null) onResize(id, px)
  }, [id, onResize])

  useEffect(() => () => { if (frameRef.current != null) cancelAnimationFrame(frameRef.current) }, [])

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    origin.current = { x: e.clientX, width }
    e.currentTarget.setPointerCapture(e.pointerId)
    setDragging(true)
  }, [width])

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    pendingPx.current = origin.current.width + (e.clientX - origin.current.x)
    if (frameRef.current == null) frameRef.current = requestAnimationFrame(flush)
  }, [dragging, flush])

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (frameRef.current != null) { cancelAnimationFrame(frameRef.current); frameRef.current = null }
    const px = pendingPx.current
    pendingPx.current = null
    if (px != null) onResize(id, px)
    setDragging(false)
  }, [dragging, id, onResize])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('Schedule_resize_column')}
      data-column-resize={id}
      className={cn('absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize touch-none select-none hover:bg-primary/40', dragging && 'bg-primary/60')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => { e.stopPropagation(); onAutoFit(id) }}
    />
  )
}

interface Props {
  columns: ColumnId[]
  columnWidths: Record<ColumnId, number>
  gridWidth: number
  onResize: (id: ColumnId, px: number) => void
  onAutoFit: (id: ColumnId) => void
}

/** Frozen column headers: sticky on both axes (its parent row is sticky top). */
const GanttGridHeader: React.FC<Props> = ({ columns, columnWidths, gridWidth, onResize, onAutoFit }) => {
  const { t } = useTranslation()
  const columnVisibility = useGanttStore((s) => s.columnVisibility)
  const toggleColumn = useGanttStore((s) => s.toggleColumn)

  return (
    <div
      // review fix (parked minor): HEADER_H is a fixed constant (48px), not a runtime value — a
      // Tailwind class replaces it; only `width` (derived from `columnWidths`) is dynamic.
      className="sticky left-0 z-40 shrink-0 flex items-stretch overflow-hidden bg-background border-b border-r h-12"
      // ui-drift: dynamic
      style={{ width: gridWidth }}
    >
      {columns.map((id) => (
        <div
          key={id}
          data-column={id}
          className={cn('relative shrink-0 px-2 pb-1 flex items-end border-r text-xs font-medium text-muted-foreground truncate', isNumericColumn(id) && 'justify-end')}
          // ui-drift: dynamic
          style={{ width: columnWidth(id, columnWidths) }}
        >
          <span className="truncate">{t(columnLabelKey[id])}</span>
          <ColumnResizeHandle id={id} width={columnWidths[id]} onResize={onResize} onAutoFit={onAutoFit} />
        </div>
      ))}
      <div className="w-7 shrink-0 flex items-end justify-center pb-0.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" aria-label={t('Schedule_columns')}>
              <Settings2 className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('Schedule_columns')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {COLUMN_ORDER.map((id) => (
              <DropdownMenuCheckboxItem
                key={id}
                checked={!!columnVisibility[id]}
                disabled={ALWAYS_VISIBLE_COLUMNS.indexOf(id) >= 0}
                onCheckedChange={() => toggleColumn(id)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(columnLabelKey[id])}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

export default React.memo(GanttGridHeader)
